<template>
	<div v-if="fields.length" class="PersonalizationFields">
		<div class="section-label">{{ $t('text.additionalPersonalizationFields') }}</div>
		<div v-for="field in fields" :key="field.id" class="field">
			<v-label class="field-label">{{ fieldLabel(field) }}<template v-if="field.required"> *</template></v-label>
			<v-select v-if="isSelect(field)"
				:model-value="modelValue[field.id]"
				:items="optionItems(field)"
				variant="outlined" density="compact" hide-details
				:data-cy="`personalization-${field.id}`"
				@update:model-value="update(field.id, $event)"
			/>
			<VuetifyDatePicker v-else-if="field.input === 'Date'"
				:model-value="modelValue[field.id]"
				:data-cy="`personalization-${field.id}`"
				@update:model-value="update(field.id, $event)"
			/>
			<v-text-field v-else
				:model-value="modelValue[field.id]"
				variant="outlined" density="compact"
				:hide-details="!fieldError(field)"
				:error-messages="fieldError(field)"
				:data-cy="`personalization-${field.id}`"
				@update:model-value="update(field.id, $event)"
			/>
		</div>
	</div>
</template>

<script>
import VuetifyDatePicker from '../../../components/datePicker/VuetifyDatePicker.vue'

// Dynamic per-product personalization fields (config from the MYS product entry).
export default {
	name: 'PersonalizationFields',
	components: { VuetifyDatePicker },
	props: {
		modelValue: { type: Object, default: () => ({}) }, // field id -> value
		fields: { type: Array, default: () => [] }, // AdditionalPersonalizationFieldConfig[]
	},
	methods: {
		update(fieldId, value) {
			this.$emit('update:modelValue', { ...this.modelValue, [fieldId]: value })
		},
		isSelect(field) {
			// Service Designer personalisation field inputType is 'List' (select) or 'Text'
			return field.input === 'List' || (field.options?.length ?? 0) > 0
		},
		optionItems(field) {
			// options come as { key, values: { de, en, ... } } from the personalisation field config
			const locale = this.$vuetify?.locale?.current ?? 'de'
			return (field.options ?? []).map(option => {
				if (typeof option === 'string') return option
				const values = option?.values ?? option
				return values?.[locale] ?? values?.de ?? values?.en ?? Object.values(values ?? {})[0] ?? ''
			}).filter(value => value !== '')
		},
		fieldLabel(field) {
			const label = field.label
			if (!label) return field.id
			if (typeof label === 'string') return label
			const locale = this.$vuetify?.locale?.current ?? 'de'
			return label[locale] ?? label.de ?? label.en ?? Object.values(label)[0] ?? field.id
		},
		fieldError(field) {
			const value = this.modelValue[field.id]
			if (field.required && (value === undefined || value === null || String(value).trim() === '')) {
				return this.$t('text.fieldRequired')
			}
			if (field.pattern && value) {
				try {
					if (!new RegExp(field.pattern).test(String(value))) {
						return this.patternMessage(field)
					}
				}
				catch { /* invalid pattern in config — do not block the user */ }
			}
			return ''
		},
		patternMessage(field) {
			const message = field.patternMessage
			if (!message) return this.$t('text.fieldInvalid')
			if (typeof message === 'string') return message
			const locale = this.$vuetify?.locale?.current ?? 'de'
			return message[locale] ?? message.de ?? message.en ?? this.$t('text.fieldInvalid')
		},
		hasErrors() {
			return this.fields.some(field => this.fieldError(field) !== '')
		},
	},
}
</script>

<style scoped>
.PersonalizationFields { margin-top: 16px; }
.section-label { font-weight: 600; font-size: 14px; margin-bottom: 8px; }
.field { margin-bottom: 12px; }
.field-label { font-size: 13px; margin-bottom: 4px; display: block; }
</style>
